/**
 * i18n — ĐỔI NGÔN NGỮ ANH/ VIỆT (yêu cầu người dùng: *"làm nút setting ngôn ngữ Anh/Việt"*).
 *
 * NGUYÊN TẮC (cố ý đơn giản để không phải viết lại cả game):
 *   · Chuỗi UI viết thẳng tại chỗ dùng: `t('Bắt đầu Wave', 'Start Wave')` — bản dịch nằm NGAY CẠNH
 *     câu gốc nên không có bảng khoá rời rạc, không sợ lệch khoá, và TypeScript bắt lỗi thiếu tham số.
 *   · NỘI DUNG game (tên class/ chiêu/ màn/ quái/ bình thuốc) thì theo **id** trong `content-en.ts`,
 *     vì id mới là thứ ổn định còn chữ Việt có thể sửa. Truy cập qua `loc.ts`
 *     (`className()`, `skillName()`, `mapName()`…) — dữ liệu gốc vẫn là tiếng Việt, không nhân đôi.
 *   · Nhãn TĨNH trong `index.html` gắn `data-i18n="vi|en"` rồi `applyStatic()` dịch (xem `applyStatic`).
 *
 * Ngôn ngữ lưu ở `localStorage` (khoá riêng, KHÔNG nằm trong save game) ⇒ đổi ngôn ngữ không đụng
 * `SAVE_VERSION` và không làm hỏng save cũ.
 */
export type Lang = 'vi' | 'en';

export const LANG_LABEL: Record<Lang, string> = { vi: 'Tiếng Việt', en: 'English' };

const STORE_KEY = 'tdk_lang';
const listeners: Array<(lang: Lang) => void> = [];

function readStored(): Lang {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(STORE_KEY) : null;
    return v === 'en' ? 'en' : 'vi';
  } catch {
    return 'vi';
  }
}

let lang: Lang = readStored();

export function getLang(): Lang {
  return lang;
}

/** Ngôn ngữ đã lưu lúc mở game ('vi' mặc định — game gốc là tiếng Việt). */
export function setLang(next: Lang): void {
  if (next === lang) return;
  lang = next;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORE_KEY, next);
  } catch {
    /* chế độ riêng tư: bỏ qua, chỉ nhớ trong phiên */
  }
  for (const fn of listeners) fn(next);
}

/** Đảo ngôn ngữ — dùng cho nút trên thanh HUD. */
export function toggleLang(): Lang {
  setLang(lang === 'vi' ? 'en' : 'vi');
  return lang;
}

/**
 * `t('Xin chào', 'Hello')` — trả câu theo ngôn ngữ đang chọn.
 * Có tham số thứ 3 tuỳ chọn `vars` để nội suy `{key}` (tránh phải cộng chuỗi rối ở nơi gọi):
 *   t('Màn {map}', 'Stage {map}', { map: '1.1' })
 */
export function t(vi: string, en: string, vars?: Record<string, string | number>): string {
  const raw = lang === 'en' ? en : vi;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`));
}

/** Đăng ký hàm chạy lại khi đổi ngôn ngữ (HUD gọi để `render()` lại toàn bộ). */
export function onLangChange(fn: (lang: Lang) => void): void {
  listeners.push(fn);
}

/**
 * DỊCH NHÃN TĨNH trong HTML: quét `[data-i18n]` (text), `[data-i18n-title]` (title),
 * `[data-i18n-aria]` (aria-label), `[data-i18n-ph]` (placeholder).
 * Giá trị viết `"vi|en"` — thiếu vế `en` thì giữ nguyên vế vi.
 */
export function applyStatic(root: ParentNode = document): void {
  const pick = (spec: string): string => {
    const [vi, en] = spec.split('|');
    return lang === 'en' && en ? en : vi;
  };
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    el.textContent = pick(el.dataset.i18n ?? '');
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-title]').forEach((el) => {
    el.title = pick(el.dataset.i18nTitle ?? '');
  });
  root.querySelectorAll<HTMLElement>('[data-i18n-aria]').forEach((el) => {
    el.setAttribute('aria-label', pick(el.dataset.i18nAria ?? ''));
  });
  root.querySelectorAll<HTMLInputElement>('[data-i18n-ph]').forEach((el) => {
    el.placeholder = pick(el.dataset.i18nPh ?? '');
  });
}
